/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { ContentEditableEvent, getUrlSync, IActionContext, RichTextComponent, Text } from '@msdyn365-commerce/core';
import { Address, BusinessPartnerProspect, CommerceProperty } from '@msdyn365-commerce/retail-proxy';
import { createProspectAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnersDataActions.g';
import { getOnlineChannelAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/StoreOperationsDataActions.g';
import { EmailRegex } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IBusinessSignUpConfig, IBusinessSignUpProps } from './business-sign-up.props.autogenerated';
import { SignUpButton, SignUpError, SignUpInput, SignUpLabel } from './components';
import { IBusinessSignUpRadioButtonOptionProps, BusinessSignUpRadioButtonOption } from './components/sign-up-radio-button';
import { FileUploader, Select, SelectOption } from '../../components';
import {
    mttCheckB2CEmailAsync,
    mttDeleteFileAsync,
    mttGetBusinessSegmentsAsync,
    mttUploadFileAsync
} from '../../proxy/DataActionExtension.g';
import { convertFileToBase64 } from '../../utilities';
import { FileType } from '../../constants';
import { OperationResultClass } from '../../proxy/DataServiceEntities.g';
import ConfirmationDialogModal from '../../components/confirmation-dialog-modal';
import { isB2CEmailExist } from '../../utilities/isB2CEmailExist';

export interface IBusinessSignUpExtendedProps extends IBusinessSignUpProps<IBusinessSignUpConfig> {
    formItems?: IFormItem[];
}

export interface IBusinessSignUpItem {
    wrapper: INodeProps;
    isAddressInput?: boolean;
    id: string;
    label: React.ReactNode;
    errorMessage: React.ReactNode;
    input?: React.ReactNode;
}

export interface IBusinessState {
    isReady: boolean;
    formItems: IFormItem[];
    addressHasErrors: boolean;
    showAddressErrors: boolean;
    hasSubmitErrors: boolean;
    hasValidationErrors: boolean;
    // MTT extension start
    showB2CEmailExistsModal: boolean;
    // MTT extension end
}

export interface IBusinessSignUpLocalAccount {
    FormWrapper: INodeProps;
    items: IBusinessSignUpItem[];
    buttons: React.ReactNode[];
    errorMessage: React.ReactNode[];
    disclaimer: React.ReactNode;
}

export interface IBusinessSignUpViewProps {
    className: string;
    businessSignUp: IModuleProps;
    ContainerWrapper: INodeProps;
    heading: React.ReactNode;
    businessSignUpForm: IBusinessSignUpLocalAccount;
    formItems: IFormItem[];
    // MTT extension start
    B2CEmailExistsModal: React.ReactNode;
    // MTT extension end
}

export interface IFormItem {
    id: string;
    type: InputType;
    label: string;
    value: string;
    maxChars?: string;
    isRequired: boolean;
    pattern?: string;
    address?: Address;
    customClass?: string;
    fieldReference?: React.RefObject<HTMLInputElement>;
    radioButtonOptions?: IBusinessSignUpRadioButtonOptionProps[];
    radioButtonGroupClass?: string;
    selectOptions?: SelectOption[];
    placeholder?: string;
    min?: string;
    max?: string;
    errorMessage?: string;
}

export enum InputType {
    // eslint-disable-next-line @typescript-eslint/no-shadow -- Text enum doesn't override the imported Text
    Text = 'text',
    Address = 'address',
    Email = 'email',
    TextArea = 'textarea',
    RadioButton = 'radiobutton',
    File = 'file',
    Numeric = 'numeric',
    Date = 'date',
    Select = 'select',
    Checkbox = 'checkbox'
}

export enum InputID {
    FirstName = 'FirstName',
    LastName = 'LastName',
    Email = 'CompanyEmail',
    CompanyName = 'CompanyName',
    Address = 'CompanyAddress',
    JobTitle = 'JobTitle',
    Size = 'CompanySize',
    Department = 'Department',
    Comment = 'Comment',
    ProspectType = 'ProspectType',
    MTT_CompanyNameArabic = 'MTT_CompanyNameArabic',
    MTT_CommercialRegisterNumber = 'MTT_CommercialRegisterNumber',
    MTT_CommercialRegisterNumberExpiryDate = 'MTT_CommercialRegisterNumberExpiryDate',
    MTT_CommercialNumberDocumentAttachment = 'MTT_CommercialNumberDocumentAttachment',
    MTT_Letter = 'MTT_Letter',
    MTT_VATCertificate = 'MTT_VATCertificate',
    MTT_VATCertificateFile = 'MTT_VATCertificateFile',
    MTT_ResponsiblePersonName = 'MTT_ResponsiblePersonName',
    MTT_Segment = 'MTT_Segment',
    MTT_Language = 'MTT_Language',
    MTT_OnlineAgreement = 'MTT_OnlineAgreement'
}

const savedInputIdsInExtProps = [
    InputID.MTT_CompanyNameArabic,
    InputID.MTT_CommercialRegisterNumber,
    InputID.MTT_CommercialRegisterNumberExpiryDate,
    InputID.MTT_CommercialNumberDocumentAttachment,
    InputID.MTT_Letter,
    InputID.MTT_VATCertificate,
    InputID.MTT_VATCertificateFile,
    InputID.MTT_ResponsiblePersonName,
    InputID.MTT_Segment,
    InputID.MTT_Language
];

const getFileTypeById = (id: string): FileType => {
    switch (id) {
        case InputID.MTT_CommercialNumberDocumentAttachment: {
            return FileType.CommercialRegisterDocument;
        }
        case InputID.MTT_Letter: {
            return FileType.Letter;
        }
        case InputID.MTT_VATCertificateFile: {
            return FileType.VATCertificate;
        }
        default: {
            return FileType.None;
        }
    }
};

const DEFAULT_MAX_FILE_SIZE_IN_MEGABYTES = 10;
const DEFAULT_SUPPORTED_FILE_TYPES = '.doc,.docx';
const BYTES_IN_MEGABYTE = 1048576;

export const renderInputSection = (
    inputs: IFormItem[],
    className: string,
    needToReset: boolean,
    onInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void,
    showErrors: boolean,
    // start of extension
    config: IBusinessSignUpConfig,
    actionContext: IActionContext,
    uploadFile: (fileType: FileType, companyName: string) => (file: File) => Promise<string>,
    deleteFile: (fileUrl: string) => Promise<OperationResultClass>,
    // end of extension
    addressForm?: React.ReactNode,
    updateAddress?: (errors: boolean, currAddress?: Address) => void
): IBusinessSignUpItem[] => {
    return inputs.map((element, index) => {
        const {
            id,
            type,
            label,
            maxChars,
            isRequired,
            pattern,
            customClass,
            value,
            fieldReference,
            radioButtonOptions,
            radioButtonGroupClass,
            selectOptions,
            placeholder,
            min,
            max,
            errorMessage
        } = element;

        const baseSignUpItem: IBusinessSignUpItem = {
            wrapper: {
                className: classnames(className, `${className}-${id}`, customClass)
            },
            id,
            label: <SignUpLabel {...{ id, forId: id, className, text: label }} />,
            errorMessage: errorMessage ? <SignUpError {...{ className, type: 'item', message: errorMessage }} /> : null
        };

        if (type === InputType.Address) {
            if (addressForm) {
                baseSignUpItem.isAddressInput = true;
                baseSignUpItem.input = (
                    <>
                        {React.cloneElement(addressForm as React.ReactElement, {
                            updateForm: updateAddress,
                            hasError: showErrors,
                            isRequired: isRequired === undefined ? false : isRequired,
                            resetAddress: needToReset
                        })}
                    </>
                );
            } else {
                // If the address slot is not filled, remove this input from the form
                inputs.splice(index, 1);
            }
        } else if (type === InputType.RadioButton) {
            baseSignUpItem.input = (
                <div className={radioButtonGroupClass}>
                    {radioButtonOptions &&
                        radioButtonOptions.map((element, index) => {
                            // MTT extension start
                            return <BusinessSignUpRadioButtonOption key={index} {...element} selected={value} />;
                            // MTT extension end
                        })}
                </div>
            );
        } else if (type === InputType.File) {
            // start of extension
            const parsedMaxFileSize = max ? parseFloat(max) : NaN;
            const maxFileSizeInMegabytes = isNaN(parsedMaxFileSize) ? DEFAULT_MAX_FILE_SIZE_IN_MEGABYTES : parsedMaxFileSize;
            const maxFileSizeInBytes = maxFileSizeInMegabytes * BYTES_IN_MEGABYTE;
            const supportedFileTypes = config.supportedFileTypes ?? DEFAULT_SUPPORTED_FILE_TYPES;
            const fileType = getFileTypeById(id);
            const companyName = inputs.find(input => input.id === InputID.CompanyName)?.value ?? '';

            baseSignUpItem.input = (
                <FileUploader
                    id={id}
                    fileTypes={supportedFileTypes}
                    maxSize={maxFileSizeInBytes}
                    uploadFile={uploadFile(fileType, companyName)}
                    deleteFile={deleteFile}
                    onChange={onInputChange}
                    isDisabled={!companyName}
                />
            );
            // end of extension
        } else if (type === InputType.Select) {
            baseSignUpItem.input = (
                <Select
                    id={id}
                    value={value}
                    className={className}
                    placeholder={placeholder}
                    options={selectOptions ?? []}
                    onChange={onInputChange}
                />
            );
        } else if (type === InputType.Checkbox && id === InputID.MTT_OnlineAgreement) {
            const termsAndConditionsUrl = getUrlSync('termsandconditions', actionContext);

            baseSignUpItem.input = (
                <div className={`${className}-${id}-container`}>
                    <SignUpInput
                        id={id}
                        type={type}
                        value={value}
                        className={className}
                        isRequired={isRequired === undefined ? false : isRequired}
                        onChange={onInputChange}
                        fieldReference={fieldReference}
                    />
                    {termsAndConditionsUrl && (
                        <a className='' href={termsAndConditionsUrl} target='_blank' rel='noopener noreferrer'>
                            Terms and conditions
                        </a>
                    )}
                </div>
            );
        } else {
            baseSignUpItem.input = (
                <SignUpInput
                    {...{
                        id,
                        type: type === InputType.Numeric ? InputType.Text : type,
                        value,
                        maxLength: maxChars,
                        pattern,
                        className,
                        isRequired: isRequired === undefined ? false : isRequired,
                        onChange: onInputChange,
                        fieldReference,
                        min
                    }}
                />
            );
        }

        return baseSignUpItem;
    });
};

/**
 *
 * Business Request Form Modules
 * Form that a business can use to submit a request to become a partner with Microsoft.
 * @extends {React.Component<IBusinessSignUpProps<IBusinessSignUpConfig>>}
 */
class BusinessSignUp extends React.Component<IBusinessSignUpExtendedProps, IBusinessState> {
    private readonly moduleClassName: string = 'ms-business-sign-up';
    private readonly prospectRadioButtonName: string = 'business-sign-up-radio-button';

    // Used as default data
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public BusinessSignUpDefaultInputs: IFormItem[] = [
        {
            id: InputID.ProspectType,
            type: InputType.RadioButton,
            label: this.props.resources.prospectTypeLabel,
            value: '',
            isRequired: true,
            customClass: 'width-100',
            radioButtonGroupClass: 'radio-option-container',
            radioButtonOptions: [
                {
                    id: `${InputID.ProspectType}-buyer`,
                    name: this.prospectRadioButtonName,
                    value: '1', // Buyer
                    label: this.props.resources.prospectTypeBuyerLabel,
                    className: `${this.moduleClassName}__form-item-radio-button-option`,
                    onChange: this._onInputChange.bind(this)
                },
                {
                    id: `${InputID.ProspectType}-seller`,
                    name: this.prospectRadioButtonName,
                    value: '2', // Seller
                    className: `${this.moduleClassName}__form-item-radio-button-option`,
                    label: this.props.resources.prospectTypeSellerLabel,
                    onChange: this._onInputChange.bind(this)
                }
            ]
        },
        {
            id: InputID.FirstName,
            type: InputType.Text,
            label: this.props.resources.firstNameLabel,
            value: '',
            isRequired: true,
            customClass: 'width-25'
        },
        {
            id: InputID.LastName,
            type: InputType.Text,
            label: this.props.resources.lastNameLabel,
            value: '',
            isRequired: true,
            customClass: 'width-25'
        },
        {
            id: InputID.Email,
            type: InputType.Email,
            label: this.props.resources.emailAddressLabel,
            value: '',
            isRequired: true,
            pattern: EmailRegex.defaultRegex.source,
            customClass: 'width-50'
        },
        {
            id: InputID.MTT_CompanyNameArabic,
            type: InputType.Text,
            label: this.props.resources.companyNameArabicLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50'
        },
        {
            id: InputID.CompanyName,
            type: InputType.Text,
            label: this.props.resources.companyLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50'
        },
        {
            id: InputID.MTT_CommercialRegisterNumber,
            type: InputType.Numeric,
            label: this.props.resources.CommercialRegisterNumberLabel,
            value: '',
            isRequired: true,
            customClass: 'width-25',
            pattern: '^(?!1234567890)(?!0000000000)[0-9]{10}$'
        },
        {
            id: InputID.MTT_CommercialRegisterNumberExpiryDate,
            type: InputType.Date,
            label: this.props.resources.CommercialRegisterNumberExpiryDateLabel,
            value: '',
            isRequired: true,
            customClass: 'width-25',
            min: new Date().toISOString().split('T')[0]
        },
        {
            id: InputID.MTT_CommercialNumberDocumentAttachment,
            type: InputType.File,
            label: this.props.resources.CommercialNumberDocumentAttachmentLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50',
            max: this.props.config.fileMaxSizeInMB?.toString()
        },
        {
            id: InputID.MTT_VATCertificate,
            type: InputType.Numeric,
            label: this.props.resources.VATCertificateNumberLabel,
            value: '',
            isRequired: false,
            customClass: 'width-50',
            pattern: '^[0-9]{15}$'
        },
        {
            id: InputID.MTT_VATCertificateFile,
            type: InputType.File,
            label: this.props.resources.VATCertificateFileLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50',
            max: this.props.config.fileMaxSizeInMB?.toString()
        },
        {
            id: InputID.MTT_Letter,
            type: InputType.File,
            label: this.props.resources.LetterLabel,
            value: '',
            isRequired: true,
            customClass: 'width-100',
            max: this.props.config.fileMaxSizeInMB?.toString()
        },
        {
            id: InputID.MTT_ResponsiblePersonName,
            type: InputType.Text,
            label: this.props.resources.ResponsiblePersonNameLabel,
            value: '',
            isRequired: false,
            customClass: 'width-50'
        },
        {
            id: InputID.MTT_Segment,
            type: InputType.Select,
            label: this.props.resources.SegmentLabel,
            value: '',
            isRequired: true,
            customClass: 'width-25',
            placeholder: 'Select a segment'
        },
        {
            id: InputID.MTT_Language,
            type: InputType.Select,
            label: this.props.resources.LanguageLabel,
            value: '',
            isRequired: true,
            customClass: 'width-25',
            placeholder: 'Select a language'
        },
        {
            id: InputID.Department,
            type: InputType.Text,
            label: this.props.resources.departmentLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100'
        },
        {
            id: InputID.JobTitle,
            type: InputType.Text,
            label: this.props.resources.jobTitleLabel,
            value: '',
            isRequired: false,
            customClass: 'width-50'
        },
        {
            id: InputID.Size,
            type: InputType.Text,
            label: this.props.resources.companySizeLabel,
            value: '',
            isRequired: false,
            customClass: 'width-50'
        },
        {
            id: InputID.Address,
            type: InputType.Address,
            label: this.props.resources.companyAddressLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100'
        },
        {
            id: InputID.Comment,
            type: InputType.TextArea,
            label: this.props.resources.commentLabel,
            value: '',
            isRequired: false,
            customClass: 'width-100'
        },
        {
            id: InputID.MTT_OnlineAgreement,
            type: InputType.Checkbox,
            label: this.props.resources.onlineAgreementLabel,
            value: '',
            isRequired: true,
            customClass: 'width-100'
        }
    ];

    private readonly formChildReference: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    private needsToReset: boolean = false;

    private readonly telemetryContent: ITelemetryContent;

    constructor(props: IBusinessSignUpExtendedProps) {
        super(props);

        this.state = {
            isReady: false,
            formItems: this.props.formItems || this._addedReference(),
            addressHasErrors: false,
            showAddressErrors: false,
            hasSubmitErrors: false,
            hasValidationErrors: false,
            // MTT extension start
            showB2CEmailExistsModal: false
            // MTT extension end
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public componentDidMount(): void {
        this.formChildReference.current?.focus();

        getOnlineChannelAsync({ callerContext: this.props.context.actionContext }, this.props.context.request.apiSettings.channelId)
            .then(data => {
                if (data?.ChannelLanguages?.length) {
                    const newForm = this.state.formItems.map(item => {
                        if (item.id === InputID.MTT_Language) {
                            const selectOptions = data.ChannelLanguages?.map(language => ({
                                id: language.LanguageId ?? '',
                                name: language.LanguageId ?? ''
                            }));
                            item.selectOptions = selectOptions;
                        }

                        return item;
                    });

                    this.setState({
                        isReady: this._isFormReady(),
                        formItems: newForm,
                        hasSubmitErrors: false
                    });
                }
            })
            .catch(err => {
                console.log(err);
                this.setState({ hasSubmitErrors: true });
            });

        mttGetBusinessSegmentsAsync({ callerContext: this.props.context.actionContext })
            .then(data => {
                if (data?.length) {
                    const newForm = this.state.formItems.map(item => {
                        if (item.id === InputID.MTT_Segment) {
                            const selectOptions = data.map(({ segment, description }) => ({ id: segment, name: description }));
                            item.selectOptions = selectOptions;
                        }

                        return item;
                    });

                    this.setState({
                        isReady: this._isFormReady(),
                        formItems: newForm,
                        hasSubmitErrors: false
                    });
                }
            })
            .catch(err => {
                console.log(err);
                this.setState({ hasSubmitErrors: true });
            });
    }

    public shouldComponentUpdate(nextProps: IBusinessSignUpExtendedProps, nextState: IBusinessState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const { config } = this.props;
        const { formItems } = this.state;
        const { className, heading } = config;

        const viewProps = {
            ...this.props,
            businessSignUp: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName, className)
            },
            ContainerWrapper: {
                className: `${this.moduleClassName}__container`
            },
            heading: (
                <Text
                    className={`${this.moduleClassName}__heading`}
                    {...heading}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            businessSignUpForm: this._renderForm(),
            formItems,
            // MTT extension start
            B2CEmailExistsModal: this._renderB2CEmailExistsModal()
            // MTT extension end
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handle the heading change event.
     * @param event - Content Editable Event.
     */
    public handleHeadingChange = (event: ContentEditableEvent): void => {
        this.props.config.heading.text = event.target.value;
    };

    private uploadFile = (fileType: FileType, companyName: string) => async (file: File) => {
        const context = { callerContext: this.props.context.actionContext };

        try {
            const encodedFileInBase64 = (await convertFileToBase64(file)) as string;

            const base64result = encodedFileInBase64.split(',')[1];
            const fileName = file.name;
            let fileUrl = '';

            const fileData = await mttUploadFileAsync(context, base64result, fileName, fileType, companyName);

            if (fileData?.fileUrl) {
                fileUrl = fileData.fileUrl;
            }

            return fileUrl;
        } catch (error) {
            throw error;
        }
    };

    private deleteFile = async (fileUrl: string) => {
        const context = { callerContext: this.props.context.actionContext };
        const result = await mttDeleteFileAsync(context, fileUrl);

        return result;
    };

    private _renderForm(): IBusinessSignUpLocalAccount {
        const { config, resources, slots, context } = this.props;
        const { showAddressErrors, hasSubmitErrors, hasValidationErrors /*, isReady*/, formItems } = this.state;
        const {
            signUpButtonArialabel,
            signUpButtonText,
            cancelButtonArialabel,
            cancelButtonText,
            errorMessage,
            partialAddressErrorMessage,
            fieldIncorrectSummaryError
        } = resources;
        const addressForm = (slots && slots.addressSlot && slots.addressSlot.length > 0 && slots.addressSlot[0]) || undefined;

        const form = {
            FormWrapper: {
                className: `${this.moduleClassName}__form `
            },
            items: renderInputSection(
                formItems,
                `${this.moduleClassName}__form-item`,
                this.needsToReset,
                this._onInputChange.bind(this),
                showAddressErrors,
                // start of extension
                this.props.config,
                context.actionContext,
                this.uploadFile,
                this.deleteFile,
                // end of extension
                addressForm,
                this._onAddressChange
            ),
            buttons: [
                <SignUpButton
                    key='continue'
                    id='continue'
                    className={`${this.moduleClassName}__create-button`}
                    ariaLabel={signUpButtonArialabel}
                    text={signUpButtonText}
                    disabled={false /*!isReady*/}
                    onClick={this._onSubmit}
                    telemetryContent={this.telemetryContent}
                />,
                <SignUpButton
                    key='cancel'
                    id='cancel'
                    className={`${this.moduleClassName}__cancel-button`}
                    ariaLabel={cancelButtonArialabel}
                    text={cancelButtonText}
                    onClick={this._onCancel}
                    telemetryContent={this.telemetryContent}
                />
            ],
            errorMessage: [
                hasSubmitErrors && <SignUpError key='error' id='error' className={this.moduleClassName} message={errorMessage} />,
                showAddressErrors && (
                    <SignUpError key='error' id='error' className={this.moduleClassName} message={partialAddressErrorMessage} />
                ),
                hasValidationErrors && (
                    <SignUpError key='error' id='error' className={this.moduleClassName} message={fieldIncorrectSummaryError} />
                )
            ],
            disclaimer: config.disclaimer && (
                <RichTextComponent className={`${this.moduleClassName}__disclaimer`} text={config.disclaimer} />
            )
        };

        if (this.needsToReset) {
            this.needsToReset = false;
        }

        return form;
    }

    private _onInputChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void {
        const newForm = this.state.formItems;
        newForm.forEach(item => {
            if (item.type === InputType.RadioButton && event.target.name === this.prospectRadioButtonName) {
                item.value = event.target.value;
                item.errorMessage = undefined;
            } else if (item.id === event.target.id) {
                if (item.type === InputType.Numeric) {
                    item.value = event.target.value.replace(/[^0-9]/g, '');
                } else if (item.type === InputType.Checkbox) {
                    item.value = String((event.target as HTMLInputElement).checked);
                } else {
                    item.value = event.target.value;
                }
                item.errorMessage = undefined;
            }
        });

        // Check if form is ready, update state object with latest values, values have changed so reset hasSubmitErrros
        this.setState({
            isReady: this._isFormReady(),
            formItems: newForm,
            hasSubmitErrors: false
        });
    }

    private _addedReference(): IFormItem[] {
        const newFormFields = this.BusinessSignUpDefaultInputs;
        newFormFields[0].fieldReference = this.formChildReference;
        return newFormFields;
    }

    private readonly _onAddressChange = (errors: boolean, currentAddress?: Address): void => {
        // Find address item
        const newForm = this.state.formItems;
        const addressItem = newForm.find(item => {
            return item.type === InputType.Address;
        });

        // If found --> set to currentAddress
        if (addressItem) {
            addressItem.address = currentAddress;

            // Update state with new values and set errors to false if address is not required field
            this.setState(
                {
                    formItems: newForm,
                    addressHasErrors: errors,
                    showAddressErrors: false
                },
                () => {
                    this.setState({ isReady: this._isFormReady() });
                }
            );
        }
    };

    private readonly _isFormReady = (): boolean => {
        const { formItems } = this.state;

        for (const currentInput of formItems) {
            // Will return FALSE if:
            //  - input field is required
            //      IF is address AND addressHasErrors === true
            //      ELSE value is undefined OR empty
            //  OR
            //  - pattern provided and value does not match pattern

            if (currentInput.isRequired) {
                if (currentInput.type === InputType.Address && this.state.addressHasErrors) {
                    return false;
                }

                if (currentInput.type === InputType.Checkbox) {
                    if (!currentInput.value || currentInput.value.toLowerCase() === 'false') {
                        return false;
                    }
                }

                if (
                    currentInput.type === InputType.File &&
                    (currentInput.id === InputID.MTT_Letter || currentInput.id === InputID.MTT_CommercialNumberDocumentAttachment) &&
                    !currentInput.value
                ) {
                    return false;
                } else if (currentInput.value === undefined || currentInput.value === '') {
                    return false;
                }
            }

            if (currentInput.id === InputID.MTT_VATCertificateFile) {
                const hasVATCertificateValue = !!formItems.find(item => item.id === InputID.MTT_VATCertificate)?.value;

                if (hasVATCertificateValue && !currentInput.value) {
                    return false;
                }
            }

            const isPatternInvalid = currentInput.pattern !== undefined && currentInput.value?.match(currentInput.pattern) === null;

            if (currentInput.id === InputID.MTT_VATCertificate && currentInput.value?.length && isPatternInvalid) {
                return false;
            }

            if (currentInput.id !== InputID.MTT_VATCertificate && isPatternInvalid) {
                return false;
            }
        }

        return true;
    };

    private readonly _validateFormItems = (): boolean => {
        let hasErrors = false;

        const { formItems } = this.state;
        const { requiredFieldMissingError, fieldIncorrectError } = this.props.resources;

        for (const currentInput of formItems) {
            if (currentInput.isRequired) {
                if (currentInput.type === InputType.Address) {
                    if (this.state.addressHasErrors) {
                        hasErrors = true;
                    }
                } else if (currentInput.type === InputType.Checkbox) {
                    if (!currentInput.value || currentInput.value.toLowerCase() === 'false') {
                        currentInput.errorMessage = requiredFieldMissingError;
                        hasErrors = true;
                    }
                } else if (currentInput.type === InputType.File && !currentInput.value) {
                    currentInput.errorMessage = requiredFieldMissingError;
                    hasErrors = true;
                } else if (currentInput.value === undefined || currentInput.value === '') {
                    currentInput.errorMessage = requiredFieldMissingError;
                    hasErrors = true;
                }
            }

            if (currentInput.id === InputID.MTT_VATCertificateFile) {
                const vatCertificateItem = formItems?.find(item => item.id === InputID.MTT_VATCertificate);
                const hasVATCertificateValue = !!vatCertificateItem?.value;
                if (hasVATCertificateValue && !currentInput.value) {
                    currentInput.errorMessage = requiredFieldMissingError;
                    hasErrors = true;
                }
            }

            const isPatternInvalid = currentInput.pattern !== undefined && currentInput.value?.match(currentInput.pattern) === null;
            if (isPatternInvalid) {
                if (currentInput.id === InputID.MTT_VATCertificate) {
                    if (currentInput.value?.length) {
                        currentInput.errorMessage = fieldIncorrectError;
                        hasErrors = true;
                    }
                } else {
                    currentInput.errorMessage = fieldIncorrectError;
                    hasErrors = true;
                }
            }
        }

        this.setState({
            formItems: formItems
        });

        return hasErrors;
    };

    // MTT extension start
    private _onB2CEmailExistsModalConfirm = async (): Promise<void> => {
        this.setState({ showB2CEmailExistsModal: false }, async () => {
            await this._submitForm();
        });
    };

    private _onB2CEmailExistsModalCancel = (): void => {
        const emailItem = this._getItem(InputID.Email);
        if (emailItem) {
            emailItem.value = '';
        }
        this.setState({
            showB2CEmailExistsModal: false,
            formItems: [...this.state.formItems]
        });
    };

    private readonly _onSubmit = async (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        // MTT extension end
        if (this.state.addressHasErrors) {
            this.setState({
                showAddressErrors: true
            });

            event?.preventDefault();
            return;
        }

        const hasValidationErrors = this._validateFormItems();
        this.setState({ hasValidationErrors });

        if (hasValidationErrors) {
            event?.preventDefault();
            return;
        }

        // MTT extension start
        const emailItem = this._getItem(InputID.Email);

        if (emailItem?.value) {
            try {
                const emailInfo = await mttCheckB2CEmailAsync({ callerContext: this.props.context.actionContext }, emailItem.value);
                if (isB2CEmailExist(emailInfo)) {
                    this.setState({ showB2CEmailExistsModal: true });
                    event.preventDefault();
                    return;
                }
            } catch (e) {
                console.error('B2C e-mail check failed', e);
            }
        }

        await this._submitForm(event);
    };

    private readonly _submitForm = async (event?: React.MouseEvent<HTMLElement, MouseEvent>): Promise<void> => {
        // MTT extension end

        const { config, context } = this.props;

        // Check for matching id and if not empty
        const firstName = this._getItem(InputID.FirstName);
        const lastName = this._getItem(InputID.LastName);
        const companyName = this._getItem(InputID.CompanyName);
        const companyEmail = this._getItem(InputID.Email);
        const companyAddress = this._getItem(InputID.Address, true);
        const jobTitle = this._getItem(InputID.JobTitle);
        const department = this._getItem(InputID.Department);
        const companySize = this._getItem(InputID.Size);
        const comments = this._getItem(InputID.Comment);
        const prospectType = this._getItem(InputID.ProspectType);

        // API gets email as part of the address object, but I do not use the email input from the address component, thus we need to copy it over
        if (companyEmail && companyEmail.value && companyAddress && companyAddress.address) {
            companyAddress.address.Email = companyEmail.value;
        }

        const extensionProperties: CommerceProperty[] = savedInputIdsInExtProps.map(id => ({
            Key: id,
            Value: { StringValue: this._getItem(id)?.value ?? '' }
        }));

        const newPartner: BusinessPartnerProspect = {
            FirstName: firstName && firstName.value,
            LastName: lastName && lastName.value,
            CompanyName: companyName && companyName.value,
            Address: companyAddress && companyAddress.address && companyAddress.address,
            JobTitle: jobTitle && jobTitle.value,
            Department: department && department.value,
            CompanySize: (companySize && Number.parseInt(companySize.value, 10)) || 0,
            Comments: comments && comments.value,
            ProspectTypeValue: (prospectType && Number.parseInt(prospectType.value, 10)) || 0,
            ExtensionProperties: extensionProperties
        };

        this.setState({
            isReady: false
        });

        // Create business partner request
        createProspectAsync({ callerContext: context.actionContext }, newPartner)
            .then(() => {
                const successPageURL =
                    (config.confirmationLink && config.confirmationLink.linkUrl && config.confirmationLink.linkUrl.destinationUrl) ||
                    getUrlSync('home', this.props.context.actionContext) ||
                    '';
                window.location.href = successPageURL;
            })
            .catch(() => {
                this.setState({
                    hasSubmitErrors: true
                });
            });
    };

    private readonly _onCancel = () => {
        // Reset values of the form
        // MTT extension start
        const cleanForm = this.state.formItems.map(input => {
            if (input.type === InputType.Checkbox) {
                return { ...input, value: 'false' };
            }
            return { ...input, value: '' };
            // MTT extension end
        });

        // Set reset flag so address input will get reset on next renderInputSection() call
        this.needsToReset = true;

        this.setState({
            isReady: false,
            formItems: cleanForm,
            addressHasErrors: true,
            showAddressErrors: false,
            hasSubmitErrors: false
        });
    };

    private readonly _getItem = (id: string, isAddress: boolean = false) => {
        if (isAddress) {
            return this.state.formItems.find(item => {
                return item.id === id && item.address;
            });
        }
        return this.state.formItems.find(item => {
            return item.id === id && item.value !== '';
        });
    };

    // MTT extension start
    private _renderB2CEmailExistsModal(): React.ReactNode {
        return (
            <ConfirmationDialogModal
                isOpen={this.state.showB2CEmailExistsModal}
                headerLabel={this.props.resources.B2CEmailExistsModalHeader}
                message={this.props.resources.B2CEmailExistsWarningModalMessage}
                confirmLabel={this.props.resources.confirmButtonLabel}
                cancelLabel={this.props.resources.cancelButtonLabel}
                onConfirm={this._onB2CEmailExistsModalConfirm}
                onCancel={this._onB2CEmailExistsModalCancel}
            />
        );
    }
    // MTT extension end
}

export default BusinessSignUp;
